import type { GenerateTextParams, IAgentRuntime } from '@elizaos/core';
import { logger, ModelType } from '@elizaos/core';
import { generateText } from 'ai';
import { createAnthropicClient } from '../providers';
import { getSmallModel, getLargeModel, getExperimentalTelemetry } from '../utils/config';
import { emitModelUsageEvent } from '../utils/events';

/**
 * TEXT_SMALL model handler
 */
export async function handleTextSmall(
  runtime: IAgentRuntime,
  {
    prompt,
    stopSequences = [],
    maxTokens,
    temperature = 0.7,
    frequencyPenalty = 0.7,
    presencePenalty = 0.7,
  }: GenerateTextParams
): Promise<string> {
  const anthropic = createAnthropicClient(runtime);
  const modelName = getSmallModel(runtime);
  const experimentalTelemetry = getExperimentalTelemetry(runtime);

  // Use provided maxTokens or default based on model
  if (maxTokens === undefined) {
    maxTokens = modelName.includes('-3-') ? 4096 : 8192;
  }

  logger.log(`[Anthropic] Using TEXT_SMALL model: ${modelName}`);

  const { text, usage } = await generateText({
    model: anthropic(modelName),
    prompt,
    system: runtime.character.system ?? undefined,
    temperature,
    maxOutputTokens: maxTokens,
    stopSequences,
    frequencyPenalty,
    presencePenalty,
    experimental_telemetry: { isEnabled: experimentalTelemetry },
  });

  if (usage) {
    emitModelUsageEvent(runtime, ModelType.TEXT_SMALL, prompt, usage);
  }

  return text;
}

/**
 * TEXT_LARGE model handler
 */
export async function handleTextLarge(
  runtime: IAgentRuntime,
  {
    prompt,
    maxTokens = 8192,
    stopSequences = [],
    temperature = 0.7,
    frequencyPenalty = 0.7,
    presencePenalty = 0.7,
  }: GenerateTextParams
): Promise<string> {
  const anthropic = createAnthropicClient(runtime);
  const modelName = getLargeModel(runtime);
  const experimentalTelemetry = getExperimentalTelemetry(runtime);

  logger.log(`[Anthropic] Using TEXT_LARGE model: ${modelName}`);

  const { text, usage } = await generateText({
    model: anthropic(modelName),
    prompt,
    system: runtime.character.system ?? undefined,
    temperature,
    maxOutputTokens: maxTokens,
    stopSequences,
    frequencyPenalty,
    presencePenalty,
    experimental_telemetry: { isEnabled: experimentalTelemetry },
  });

  if (usage) {
    emitModelUsageEvent(runtime, ModelType.TEXT_LARGE, prompt, usage);
  }

  return text;
}
